import { useCallback, useSyncExternalStore } from "react";

// Tema chiaro/scuro (Davide 2026-10-27: «versione dark attivabile con
// tastino»). Fonte unica: la classe ``dark`` su <html>, che cambia le
// variabili CSS dei colori (globals.css). La scelta resta in localStorage.
//
// Uno store a livello di modulo e non uno useState per componente: il tasto
// nella barra e quello in Impostazioni sono montati insieme, e con due stati
// separati uno dei due mostrerebbe il tema sbagliato.
//
// Di default si parte CHIARI anche se il sistema e' scuro: il tema scuro si
// accende col tasto, non arriva da solo (prima seguiva il sistema, ma nessuna
// pagina era fatta per il buio e non si notava).

const STORAGE_KEY = "om.theme";
export type Theme = "light" | "dark";

function leggiSalvato(): Theme {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "dark" ? "dark" : "light";
  } catch {
    // Storage bloccato (modalita' privata): si resta chiari.
    return "light";
  }
}

let corrente: Theme = typeof window === "undefined" ? "light" : leggiSalvato();
const ascoltatori = new Set<() => void>();

function applica(theme: Theme): void {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("dark", theme === "dark");
}

function imposta(theme: Theme): void {
  corrente = theme;
  applica(theme);
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Non salvato: vale fino alla chiusura della pagina.
  }
  ascoltatori.forEach((f) => f());
}

function iscrivi(f: () => void): () => void {
  ascoltatori.add(f);
  return () => ascoltatori.delete(f);
}

export function useTheme(): {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggle: () => void;
} {
  const theme = useSyncExternalStore(
    iscrivi,
    () => corrente,
    (): Theme => "light",
  );
  const setTheme = useCallback((next: Theme) => imposta(next), []);
  const toggle = useCallback(() => imposta(corrente === "dark" ? "light" : "dark"), []);
  return { theme, setTheme, toggle };
}

// Boot-time apply (chiamato in main.tsx prima del primo render React) per
// evitare il lampo chiaro all'avvio quando il tema scelto e' lo scuro.
export function applyInitialTheme(): void {
  corrente = leggiSalvato();
  applica(corrente);
}
